import { useRef, useState } from 'react';
import type { LotTargetCountry } from '@produzionejb/types';
import { useLots } from '../lib/lots-api';
import { PopoverPortal } from './PopoverPortal';

export function LotPicker({
  value,
  valueCode,
  onChange,
  disabled,
  targetCountry,
  includeAllStatuses = false,
  allowClear = false,
  placeholder = '— scegli —',
}: {
  value: string;
  valueCode?: string;
  // onChange riceve anche productId per consentire al chiamante (es. MSS/MSF form)
  // di derivare il catalogo SKU per il prodotto del lotto. Con allowClear=true e
  // selezione "Tutti i lotti", i 3 valori sono stringa vuota.
  onChange: (id: string, code: string, productId: string) => void;
  disabled?: boolean;
  // Se passato, il picker mostra solo lotti con Lot.targetCountry == questo valore
  // OPPURE targetCountry == null (lotti non pre-allocati a un mercato).
  // Usato da MSS (ES) e MSF (FR) per filtrare la lista lotti.
  targetCountry?: LotTargetCountry;
  // Filter use: include anche lotti EXHAUSTED/BLOCKED/ARCHIVED (utile in
  // /stock-movements per filtrare movimenti storici di lotti esauriti).
  includeAllStatuses?: boolean;
  // Filter use: mostra opzione "— Tutti i lotti —" in cima alla lista per
  // svuotare la selezione (onChange chiamato con stringhe vuote).
  allowClear?: boolean;
  // Testo del button quando nessun lotto e' selezionato.
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');
  const lotsQ = useLots({
    pageSize: 20,
    ...(search.trim() && { search: search.trim() }),
    ...(!includeAllStatuses && { status: 'ACTIVE' as const }),
    ...(targetCountry && { targetCountry }),
  });
  const display = valueCode || (value ? value.slice(0, 8) : '');
  return (
    <div className="relative">
      <button
        ref={anchorRef}
        type="button"
        className="w-full rounded-md border border-slate-300 px-2 py-1 text-left text-xs"
        onClick={() => !disabled && setOpen((v) => !v)}
        disabled={disabled}
      >
        {display || placeholder}
      </button>
      <PopoverPortal anchorRef={anchorRef} open={open} onClose={() => setOpen(false)}>
        <div className="w-80 rounded-md border border-slate-300 bg-white p-2 shadow-lg">
          <input
            autoFocus
            type="text"
            className="mb-2 block w-full rounded border border-slate-300 px-2 py-1 text-xs"
            placeholder="Cerca lotto..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <ul className="max-h-72 overflow-y-auto text-xs">
            {allowClear && (
              <li
                className="cursor-pointer rounded p-1 text-slate-500 italic hover:bg-slate-100"
                onClick={() => {
                  onChange('', '', '');
                  setOpen(false);
                  setSearch('');
                }}
              >
                — Tutti i lotti —
              </li>
            )}
            {lotsQ.isLoading && <li className="p-1 text-slate-400">Caricamento...</li>}
            {lotsQ.data && lotsQ.data.items.length === 0 && (
              <li className="p-1 text-slate-400">Nessun risultato</li>
            )}
            {lotsQ.data?.items.map((l) => {
              // Display "vivo": somma positions multi-prodotto se popolate, altrimenti
              // fallback su Lot.residualQty legacy. Le LotPosition sono il modello
              // canonico (ADR 0008), Lot.productCode legacy e' nascosto perche'
              // quasi sempre = DA_CATEGORIZZARE (placeholder) e confonde l'operatore.
              const hasPositions = l.positions.length > 0;
              const positionsTotal = l.positions.reduce((s, p) => s + p.residualQty, 0);
              const displayResidual = hasPositions ? positionsTotal : Number(l.residualQty);
              const uncategorized = !hasPositions && l.productCode === 'DA_CATEGORIZZARE';
              return (
                <li
                  key={l.id}
                  className="cursor-pointer rounded p-1 hover:bg-slate-100"
                  onClick={() => {
                    onChange(l.id, l.code, l.productId);
                    setOpen(false);
                    setSearch('');
                  }}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-mono">{l.code}</span>
                    <span className="text-slate-500">res {displayResidual.toFixed(0)}g</span>
                  </div>
                  {hasPositions ? (
                    <div className="ml-3 mt-0.5 text-[10px] leading-snug text-slate-600">
                      {l.positions.map((p, i) => (
                        <span key={p.productId}>
                          {i > 0 && <span className="mx-1 text-slate-300">·</span>}
                          <span className="font-medium">{p.productName}</span>
                          <span className="ml-1 text-slate-500">{p.residualQty.toFixed(0)}g</span>
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="ml-3 mt-0.5 text-[10px]">
                      {uncategorized ? (
                        <span className="rounded bg-amber-100 px-1 py-0.5 font-semibold uppercase tracking-wide text-amber-800">
                          non categorizzato
                        </span>
                      ) : (
                        <span className="text-slate-500">{l.productName}</span>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      </PopoverPortal>
    </div>
  );
}
